export type TaskStatus = 'pending' | 'in_progress' | 'completed';

export const TASK_TITLE_MAX_LENGTH = 100;

export interface Task {
  id: string;
  event_id: string;
  title: string;
  description: string | null;
  status: TaskStatus;
  deadline: string | null;
  hours_submitted: boolean;
  created_at: string;
  /** Co-assignee profile ids (from `tasks.assigned_volunteers`) */
  assigned_volunteers: string[];
  /** Department pools for this task (from `tasks.assigned_departments`) */
  assigned_departments: string[];
}

/** Normalize a Supabase row into a Task (defaults array columns). */
export function normalizeTaskRow(row: Record<string, unknown>): Task {
  const r = row as Record<string, unknown>;
  return {
    id: String(r.id),
    event_id: String(r.event_id),
    title: String(r.title ?? ''),
    description: (r.description as string | null) ?? null,
    status: r.status as TaskStatus,
    deadline: (r.deadline as string | null) ?? null,
    hours_submitted: Boolean(r.hours_submitted),
    created_at: String(r.created_at ?? ''),
    assigned_volunteers: Array.isArray(r.assigned_volunteers)
      ? (r.assigned_volunteers as string[])
      : [],
    assigned_departments: Array.isArray(r.assigned_departments)
      ? (r.assigned_departments as string[])
      : [],
  };
}

export function formatDeadline(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function hasExplicitTime(iso: string): boolean {
  return /[T\s]\d{2}:\d{2}/.test(iso);
}

/** e.g. "Fri, Apr 18, 2026, 3:00 PM" */
export function formatDeadlineWithWeekday(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const baseOptions: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };

  if (!hasExplicitTime(iso)) {
    return d.toLocaleDateString(undefined, baseOptions);
  }

  return d.toLocaleString(undefined, {
    ...baseOptions,
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function isOverdue(iso: string | null, status: TaskStatus): boolean {
  if (!iso || status === 'completed') return false;
  return new Date(iso) < new Date();
}

/** Overdue first, then soonest deadline, then older created first */
export function sortTasksForUpNext(list: Task[]): Task[] {
  return [...list].sort((a, b) => {
    const aOver = isOverdue(a.deadline, a.status);
    const bOver = isOverdue(b.deadline, b.status);
    if (aOver !== bOver) return aOver ? -1 : 1;
    const aTime = a.deadline ? new Date(a.deadline).getTime() : Number.POSITIVE_INFINITY;
    const bTime = b.deadline ? new Date(b.deadline).getTime() : Number.POSITIVE_INFINITY;
    if (aTime !== bTime) return aTime - bTime;
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });
}
